// components/analytics/sections/RequestAnalytics.tsx
import { useState } from "react";
import { Row, Col, Card, Radio, Typography, Statistic, Tooltip } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, FileTextOutlined, ClockCircleOutlined, RobotOutlined, InfoCircleOutlined, LoadingOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { TrendLineChart } from "../components/charts/TrendLineChart";
import { ActivityTrendChart } from "../components/charts/ActivityTrendChart";
import { BarChart } from "../components/charts/BarChart";
import type { RequestAnalytics as RequestAnalyticsType } from "../../../types/analytics";
import { useAnalytics } from "../../../contexts/AnalyticsContext";
import { useTheme } from "../../../contexts/ThemeContext";
import { useTranslation } from "react-i18next";

const { Text } = Typography;

interface RequestAnalyticsProps {
  data?: RequestAnalyticsType | null;
  loading?: boolean;
}

type ActivityPeriod = 'hourly' | 'daily' | 'weekly' | 'monthly';

// Componente ModernStatCard para RequestAnalytics
const ModernStatCard: React.FC<{
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  suffix?: string | React.ReactNode;
  prefix?: string | React.ReactNode;
  precision?: number;
  loading?: boolean;
  info?: string;
  theme?: 'dark' | 'light';
}> = ({ title, value, icon, color, suffix, prefix, precision, loading, info, theme = 'light' }) => (
  <div
    style={{
      background: theme === 'dark' 
        ? '#23272f' 
        : 'rgba(255, 255, 255, 0.95)',
      borderRadius: 16,
      padding: '20px',
      boxShadow: theme === 'dark'
        ? '0 2px 8px rgba(0,0,0,0.32)'
        : '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)',
      backdropFilter: 'blur(12px)',
      border: theme === 'dark'
        ? '1px solid #23272f'
        : '1px solid rgba(0, 0, 0, 0.06)',
      position: 'relative',
      overflow: 'hidden',
      height: '120px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      transition: 'background 0.2s, border 0.2s, box-shadow 0.2s'
    }}
  >
    {/* Gradiente decorativo */}
    <div
      style={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: '60px',
        height: '60px',
        background: theme === 'dark'
          ? `linear-gradient(135deg, ${color}33, #23272f00)`
          : `linear-gradient(135deg, ${color}20, ${color}10)`,
        borderRadius: '0 16px 0 60px'
      }}
    />
    
    {/* Header con icono */}
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 1 }}>
      <Text style={{ 
        fontSize: '13px', 
        fontWeight: 600, 
        color: theme === 'dark' ? '#d9d9d9' : '#595959', 
        margin: 0 
      }}>
        {title}
      </Text>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '8px' 
      }}>
        {info && (
          <Tooltip title={info}>
            <InfoCircleOutlined style={{ 
              color: theme === 'dark' ? '#8c8c8c' : '#bfbfbf', 
              fontSize: '12px' 
            }} />
          </Tooltip>
        )}
        <div
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            background: `linear-gradient(135deg, ${color}, ${color}dd)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: '14px',
            boxShadow: `0 2px 8px ${color}40`
          }}
        >
          {icon}
        </div>
      </div>
    </div>

    {/* Valor principal */}
    <div style={{ zIndex: 1 }}>
      <Statistic
        value={value}
        prefix={prefix}
        suffix={suffix}
        precision={precision}
        loading={loading}
        valueStyle={{ 
          fontSize: '24px', 
          fontWeight: 700, 
          color: theme === 'dark' ? '#fff' : '#262626',
          margin: 0
        }}
      />
    </div>
  </div>
);

// Componente para cards con información adicional
const InfoStatCard: React.FC<{
  title: string;
  value: number | string;
  info: string;
  icon: React.ReactNode;
  color: string;
  prefix?: string | React.ReactNode;
  precision?: number;
  loading?: boolean;
  theme?: 'dark' | 'light';
}> = ({ title, value, info, icon, color, prefix, precision, loading, theme = 'light' }) => (
  <div
    style={{
      background: theme === 'dark' 
        ? '#23272f' 
        : 'rgba(255, 255, 255, 0.95)',
      borderRadius: 16,
      padding: '20px',
      boxShadow: theme === 'dark'
        ? '0 2px 8px rgba(0,0,0,0.32)'
        : '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)',
      backdropFilter: 'blur(12px)',
      border: theme === 'dark'
        ? '1px solid #23272f'
        : '1px solid rgba(0, 0, 0, 0.06)',
      position: 'relative',
      overflow: 'hidden',
      height: '120px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      transition: 'background 0.2s, border 0.2s, box-shadow 0.2s'
    }}
  >
    {/* Gradiente decorativo */}
    <div
      style={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: '60px',
        height: '60px',
        background: theme === 'dark'
          ? `linear-gradient(135deg, ${color}33, #23272f00)`
          : `linear-gradient(135deg, ${color}20, ${color}10)`,
        borderRadius: '0 16px 0 60px'
      }}
    />
    
    {/* Header con icono e info */}
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Text style={{ 
          fontSize: '13px', 
          fontWeight: 600, 
          color: theme === 'dark' ? '#d9d9d9' : '#595959', 
          margin: 0 
        }}>
          {title}
        </Text>
        <Tooltip title={info}>
          <InfoCircleOutlined style={{ 
            color: theme === 'dark' ? '#8c8c8c' : '#bfbfbf', 
            fontSize: '12px' 
          }} />
        </Tooltip>
      </div>
      <div
        style={{
          width: '32px',
          height: '32px',
          borderRadius: '8px',
          background: `linear-gradient(135deg, ${color}, ${color}dd)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontSize: '14px',
          boxShadow: `0 2px 8px ${color}40`
        }}
      >
        {icon}
      </div>
    </div>

    {/* Valor principal */}
    <div style={{ zIndex: 1 }}>
      <Statistic
        value={value}
        prefix={prefix}
        precision={precision}
        loading={loading}
        valueStyle={{ 
          fontSize: '24px', 
          fontWeight: 700, 
          color: theme === 'dark' ? '#fff' : '#262626',
          margin: 0
        }}
      />
    </div>
  </div>
);

const defaultData: RequestAnalyticsType = {
  total: 0,
  loads: {
    total: 0,
    approved: 0,
    rejected: 0,
  },
  withdraws: {
    total: 0,
    approved: 0,
    rejected: 0,
  },
  processing: {
    automatic: 0,
    manual: 0,
    reprocessed: 0,
  },
  trends: [],
  rejectionReasons: [],
  rejectionTrends: [],
  avgDepositsPerDay: 0,
  avgWithdrawsPerDay: 0,
  mostActiveLoadHour: { hour: 0, count: 0 },
  mostActiveWithdrawHour: { hour: 0, count: 0 },
  hourlyActivity: [],
  dailyActivity: [],
  weeklyActivity: [],
  monthlyActivity: []
};

export const RequestAnalytics: React.FC<RequestAnalyticsProps> = ({
  data = defaultData,
  loading = false,
}) => {
  const { t } = useTranslation();
  const [showRejectionTrend, setShowRejectionTrend] = useState<boolean>(false);

  console.log('[RequestAnalytics] data received:', JSON.stringify({ total: data?.total, loads: data?.loads, withdraws: data?.withdraws }));
  const [activityPeriod, setActivityPeriod] = useState<ActivityPeriod>('hourly');
  const { theme } = useTheme();

  const loadApprovalRate =
    data.loads.total > 0 ? (data.loads.approved / data.loads.total) * 100 : 0;

  const withdrawApprovalRate =
    data.withdraws.total > 0
      ? (data.withdraws.approved / data.withdraws.total) * 100
      : 0;

  const automaticRate =
    data.total > 0 ? (data.processing.automatic / data.total) * 100 : 0;
  const { filters } = useAnalytics();
  
  // Prepare rejection trends data for the chart
const rejectionTrendData = data.rejectionTrends?.map(item => ({
  date: item.date,
  value: item.count, // ✅ CORREGIDO: usar 'value' para que coincida con el tipo esperado
  type: item.reason || 'Unknown',
})) || [];

const transformedWeeklyData = data?.weeklyActivity?.map(item => ({
  week: typeof item.week === 'string' ? parseInt(item.week, 10) : item.week,
  count: item.count,
  type: item.type
})) || [];
  
  const getActivityPeriodTitle = () => {
    switch (activityPeriod) {
      case 'hourly': return t("analytics.requestAnalytics.charts.hourlyActivity.title");
      case 'daily': return t("analytics.requestAnalytics.charts.dailyActivity.title");
      case 'weekly': return t("analytics.requestAnalytics.charts.weeklyActivity.title");
      case 'monthly': return t("analytics.requestAnalytics.charts.monthlyActivity.title");
      default: return t("analytics.requestAnalytics.charts.hourlyActivity.title");
    }
  };

  return (
    <div>
      {/* Cards principales con diseño moderno */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <ModernStatCard
            title={t("analytics.requestAnalytics.cards.totalRequests")}
            value={data.total}
            icon={<FileTextOutlined />}
            color="#52c41a"
            loading={loading}
            info="Total de solicitudes procesadas en el período"
            theme={theme}
          />
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <ModernStatCard
            title={t("analytics.requestAnalytics.cards.totalLoads")}
            value={data.loads.total}
            icon={<CheckCircleOutlined />}
            color="#1890ff"
            suffix={`(${loadApprovalRate.toFixed(1)}% aprobadas)`}
            loading={loading}
            info="Solicitudes de carga procesadas"
            theme={theme}
          />
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <ModernStatCard
            title={t("analytics.requestAnalytics.cards.totalWithdraws")}
            value={data.withdraws.total}
            icon={<CloseCircleOutlined />}
            color="#fa8c16"
            suffix={`(${withdrawApprovalRate.toFixed(1)}% aprobadas)`}
            loading={loading}
            info="Solicitudes de retiro procesadas"
            theme={theme}
          />
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <ModernStatCard
            title={t("analytics.requestAnalytics.cards.automaticProcessing")}
            value={data.processing.automatic}
            icon={<RobotOutlined />}
            color="#722ed1"
            suffix={`(${automaticRate.toFixed(1)}%)`}
            loading={loading}
            info="Procesamiento automático vs manual"
            theme={theme}
          />
        </Col>
      </Row>

      {/* Segunda fila de cards informativos */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <InfoStatCard
            title="Promedio diario de cargas"
            value={data.avgDepositsPerDay}
            icon={<ThunderboltOutlined />}
            color="#13c2c2"
            precision={1}
            loading={loading}
            info="Promedio de solicitudes de carga por día"
            theme={theme}
          />
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <InfoStatCard
            title="Promedio diario de retiros"
            value={data.avgWithdrawsPerDay}
            icon={<LoadingOutlined />}
            color="#eb2f96"
            precision={1}
            loading={loading}
            info="Promedio de solicitudes de retiro por día"
            theme={theme}
          />
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <InfoStatCard
            title="Hora más activa (cargas)"
            value={`${data.mostActiveLoadHour.hour}:00`}
            icon={<ClockCircleOutlined />}
            color="#faad14"
            loading={loading}
            info={`${data.mostActiveLoadHour.count} solicitudes en esa hora`}
            theme={theme}
          />
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <InfoStatCard
            title="Hora más activa (retiros)"
            value={`${data.mostActiveWithdrawHour.hour}:00`}
            icon={<ClockCircleOutlined />}
            color="#f5222d"
            loading={loading}
            info={`${data.mostActiveWithdrawHour.count} solicitudes en esa hora`}
            theme={theme}
          />
        </Col>
      </Row>

      {/* Resto del contenido (charts) con cards modernos */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FileTextOutlined style={{ color: '#52c41a' }} />
                {t("analytics.requestAnalytics.charts.requestTrends.title")}
              </div>
            }
            loading={loading}
            style={{
              background: 'rgba(255, 255, 255, 0.95)',
              borderRadius: 16,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(0, 0, 0, 0.06)'
            }}
          >
              <TrendLineChart
              data={(() => {
                const filteredTrends = data?.trends
                  ?.filter((trend) => !filters.accountName || trend.accountName === filters.accountName)
                  ?.map((trend) => ({
                    date: trend.date,
                    value: trend.count || 0,
                    type: trend.type === 'LOAD' 
                      ? (filters.accountName ? "Cargas" : `Cargas (${trend.accountName || "Default"})`)
                      : (filters.accountName ? "Retiros" : `Retiros (${trend.accountName || "Default"})`),
                    accountName: trend.accountName,
                  })) || [];
                
                
                return filteredTrends;
              })()}
                xField="date"
                yField="value"
                seriesField="type"
              />
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '8px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <ClockCircleOutlined style={{ color: '#1890ff' }} />
                  {getActivityPeriodTitle()}
                </div>
              <Radio.Group 
                value={activityPeriod} 
                onChange={(e) => setActivityPeriod(e.target.value)}
                size="small"
              >
                  <Radio.Button value="hourly">H</Radio.Button>
                  <Radio.Button value="daily">D</Radio.Button>
                  <Radio.Button value="weekly">S</Radio.Button>
                  <Radio.Button value="monthly">M</Radio.Button>
              </Radio.Group>
              </div>
            }
            loading={loading}
            style={{
              background: 'rgba(255, 255, 255, 0.95)',
              borderRadius: 16,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(0, 0, 0, 0.06)'
            }}
          >
<ActivityTrendChart 
              hourlyData={data.hourlyActivity || []}
              dailyData={data.dailyActivity || []}
              weeklyData={transformedWeeklyData}
              monthlyData={data.monthlyActivity || []}
  period={activityPeriod}
  loading={loading}
/>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={24}>
          <Card
            title={
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                gap: '8px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CloseCircleOutlined style={{ color: '#fa8c16' }} />
                  {showRejectionTrend 
                    ? t("analytics.requestAnalytics.charts.rejectionTrends.title")
                    : t("analytics.requestAnalytics.charts.rejectionReasons.title")
                  }
                </div>
                <Radio.Group
                  value={showRejectionTrend ? 'trend' : 'reasons'}
                  onChange={(e) => setShowRejectionTrend(e.target.value === 'trend')}
                  size="small"
                >
                  <Radio.Button value="reasons">Razones</Radio.Button>
                  <Radio.Button value="trend">Tendencia</Radio.Button>
                </Radio.Group>
              </div>
            }
            loading={loading}
            style={{
              background: 'rgba(255, 255, 255, 0.95)',
              borderRadius: 16,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(0, 0, 0, 0.06)'
            }}
          >
            {showRejectionTrend ? (
              <TrendLineChart 
                data={rejectionTrendData}
                xField="date"
                yField="value"
                seriesField="type"
              />
            ) : (
              <BarChart 
                data={data.rejectionReasons}
                xField="reason"
                yField="count"
                direction="horizontal"
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};